"use client";
import { useState, useEffect, useRef } from "react";
import { motion, useAnimate, AnimationSequence, stagger } from "motion/react";
import Link from "next/link";
import { usePathname } from "next/navigation";

import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "./accordion";
import Hamburger, { HAMBURGER_VARIANTS } from "@/components/navigation/hamburger";
import { ScrollArea, ScrollBar } from "../ui/scroll-area";
import { Overlay } from "./overlay";
import { type NavMenuProps, getHref, isExternalLink, MenuItemType } from "./navmenu";
import { Logo } from "../logo";
import { Button } from "../ui/button";
import { appendAttributionToUrl } from "@/lib/attribution";
import { CONVERSION_PAGE_PATH, scrollToSignupForm } from "@/lib/conversion-page";

const useMenuAnimation = (isOpen: boolean) => {
  const [scope, animate] = useAnimate();
  const isInitial = useRef(true);
  useEffect(() => {
    const runAnimation = async () => {
      try {
        if (isOpen) {
          const sequence: AnimationSequence = [
            ["div.nav", SIDENAV_VARIANTS.open, { duration: 0.6, type: "spring" }],
            ["div.nav-item", LISTITEM_VARIANTS.open, { delay: stagger(0.1), at: "+0.1" }],

            ["span.top", HAMBURGER_VARIANTS.top.open, { duration: 0.7, at: 0 }],
            ["span.middle", HAMBURGER_VARIANTS.middle.open, { duration: 0.7, at: 0 }],
            ["span.bottom", HAMBURGER_VARIANTS.bottom.open, { duration: 0.7, at: 0 }],
          ];
          await animate(sequence);
        } else {
          const stage1: AnimationSequence = [
            ["div.nav-item", LISTITEM_VARIANTS.close, { at: 0 }],
            [
              "div.nav",
              SIDENAV_VARIANTS.close,
              {
                duration: 0.5,
                at: 0.5,
              },
            ],

            [
              "span.top",
              isInitial.current ? HAMBURGER_VARIANTS.top.initial : HAMBURGER_VARIANTS.top.close,
              { at: 0, duration: 0.7 },
            ],
            [
              "span.middle",
              isInitial.current
                ? HAMBURGER_VARIANTS.middle.initial
                : HAMBURGER_VARIANTS.middle.close,
              { at: 0, duration: 0.7 },
            ],
            [
              "span.bottom",
              isInitial.current
                ? HAMBURGER_VARIANTS.bottom.initial
                : HAMBURGER_VARIANTS.bottom.close,
              { at: 0, duration: 0.7 },
            ],
          ];
          await animate(stage1);
        }
        if (isInitial.current) {
          isInitial.current = false;
        }
      } catch (error) {
        console.error("Animation error:", error);
      }
    };

    runAnimation();
  }, [isOpen, isInitial, animate]);

  return scope;
};

const SIDENAV_VARIANTS = {
  open: {
    height: "500px",
    width: "100vw",
    paddingTop: "64px",
    paddingBottom: "64px",
    background: "#FFFFFF",
    top: 0,
    x: "0px",
    y: "0px",
  },
  close: {
    height: "0",
    width: "0",
    paddingTop: 0,
    paddingBottom: 0,
    background: "#FFFFFF",
    x: "50px",
    y: "-100px",
  },

  initial: {
    height: "0",
    width: "0",
  },
};

const LISTITEM_VARIANTS = {
  open: {
    opacity: 1,
    y: "0%",
  },
  close: {
    opacity: 0,
    y: "-10%",
  },
};

function MenuItem({
  item,
  parentPath,
  onClick,
}: {
  item: MenuItemType;
  parentPath?: string;
  onClick: () => void;
}) {
  if (item.children) {
    return (
      <AccordionItem asChild value={item._key}>
        <motion.div
          style={LISTITEM_VARIANTS.close}
          whileHover={{
            color: "#4B5563",
          }}
          className="w-full capitalize nav-item text-gray-600"
        >
          <AccordionTrigger aria-label={item.ariaLabel || item.text}>{item.text}</AccordionTrigger>
          <AccordionContent>
            {item.children.map((child) => (
              <MenuItem
                //@ts-expect-error -- types mismatch
                item={child}
                parentPath={parentPath}
                key={child._key}
                onClick={onClick}
              />
            ))}
          </AccordionContent>
        </motion.div>
      </AccordionItem>
    );
  } else {
    const href = getHref(item.link);
    const isExternal = isExternalLink(item.link);
    return (
      <motion.div
        style={LISTITEM_VARIANTS.open}
        whileHover={{
          color: "#4B5563",
        }}
        className="w-full capitalize nav-item text-sm py-4 text-gray-600"
      >
        {isExternal ? (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium"
            onClick={onClick}
            aria-label={item.ariaLabel || item.text}
          >
            {item.text}
          </a>
        ) : (
          <Link
            href={href}
            className="font-medium"
            onClick={onClick}
            aria-label={item.ariaLabel || item.text}
          >
            {item.text}
          </Link>
        )}
        <p>{item.description}</p>
      </motion.div>
    );
  }
}

const SideNav = ({ menuItems }: NavMenuProps) => {
  const [open, setOpen] = useState(false);
  const isConversionPage = usePathname() === CONVERSION_PAGE_PATH;
  const handleClick = () => {
    setOpen(false);
  };
  const toggleNav = () => setOpen((prev) => !prev);
  const scope = useMenuAnimation(open);

  // Create home menu item
  const homeMenuItem: MenuItemType = {
    _type: "menuItem",
    _key: "home",
    text: "Home",
    ariaLabel: "Go to Home page",
    children: null,
    link: {
      type: "internal",
      internal: {
        title: null,
        slug: {
          _type: "slug",
          current: "/",
        },
      },
    },
  };

  // On the conversion page the menu has no items left, so opening the drawer
  // would show an empty panel. Show the sign-up button on its own instead.
  if (isConversionPage) {
    return (
      <div className="font-outfit w-fit block xl:hidden">
        <Button radius="full" size="lg" onClick={scrollToSignupForm}>
          Sign up
        </Button>
      </div>
    );
  }

  return (
    <div className="font-outfit relative w-fit z-50 block xl:hidden h-full " ref={scope}>
      <Hamburger active={open} onActiveChange={toggleNav} />
      <motion.div
        style={SIDENAV_VARIANTS.initial}
        className={`shadow-lg nav fixed top-0 bottom-0 right-0 rounded-lg overflow-hidden z-50`}
      >
        <div className="absolute top-0 p-4 border-b-border-200 border-b w-full bg-white">
          <Logo theme="dark" />
        </div>
        <ScrollArea className="h-full px-4 -z-10 py-10">
          <Accordion type="single" collapsible className="">
            {/* Add Home menu item first */}
            <MenuItem item={homeMenuItem} key="home" onClick={handleClick} />
            {menuItems.map((item) => (
              <MenuItem item={item} key={item._key} onClick={handleClick} />
            ))}
          </Accordion>
          <ScrollBar />
        </ScrollArea>
        <div className="absolute bottom-0 p-4 border-t-border-200 border-t w-full flex items-center justify-center gap-4 bg-white">
          <Button
            variant="primary"
            radius="full"
            size="lg"
            className="w-full"
            onClick={() =>
              (window.location.href = appendAttributionToUrl(
                `${process.env.NEXT_PUBLIC_USER_APP}/sign-in`,
              ))
            }
          >
            Sign in
          </Button>
          <Button
            variant="default"
            radius="full"
            size="lg"
            className="w-full"
            onClick={() =>
              (window.location.href = appendAttributionToUrl(
                `${process.env.NEXT_PUBLIC_USER_APP}/sign-up`,
              ))
            }
          >
            Sign up
          </Button>
        </div>
      </motion.div>
      <Overlay open={open} onClose={toggleNav} />
    </div>
  );
};

export default SideNav;
